import React, { useState, useEffect, useCallback, useMemo } from "react";
import moment, { Moment } from "moment";
import styled, { keyframes, css } from "styled-components";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faCalendarDays,
  faXmark,
  faClipboardList,
  faMapMarkerAlt,
  faTag,
  faArrowRight,
  faCircleCheck,
} from "@fortawesome/pro-light-svg-icons";
import { Spinner } from "react-bootstrap";
import { getUserData } from "src/redux/selectors/user";
import { FirebaseDataService } from "src/services/Firebase/data";
import { setPageNameHeader } from "src/redux/actions/header";
import { Ticket } from "src/types";
import { colors } from "src/styles";

// ─── Status helpers ──────────────────────────────────────────────────────────

const STATUS_PALETTE: Record<string, { bg: string; text: string; border: string }> = {
  New: { bg: "#EBF5FF", text: "#208AFD", border: "#208AFD" },
  "In progress": { bg: "#FFF4EB", text: "#FF7A00", border: "#FF7A00" },
  Completed: { bg: "#EEFBEE", text: "#58A942", border: "#58A942" },
  Approved: { bg: "#F0FBF0", text: "#81C17D", border: "#81C17D" },
};

const getStatusPalette = (status?: string) =>
  STATUS_PALETTE[status ?? ""] ?? { bg: "#F5F5F5", text: "#718096", border: "#CBCBCB" };

// ─── Calendar math ───────────────────────────────────────────────────────────

const WEEK_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function buildCalendarDays(month: Moment): Moment[] {
  const start = month.clone().startOf("month").startOf("week");
  const end = month.clone().endOf("month").endOf("week");
  const days: Moment[] = [];
  const cursor = start.clone();
  while (cursor.isSameOrBefore(end, "day")) {
    days.push(cursor.clone());
    cursor.add(1, "day");
  }
  return days;
}

// ─── Component ───────────────────────────────────────────────────────────────

const Schedules: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: loggedInUserId, isManager } = useSelector(getUserData);

  const [currentMonth, setCurrentMonth] = useState<Moment>(moment().startOf("month"));
  const [selectedDay, setSelectedDay] = useState<Moment | null>(moment().startOf("day"));
  const [workOrders, setWorkOrders] = useState<Ticket[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch(setPageNameHeader("Schedules"));
  }, []);

  const fetchWorkOrders = useCallback(async () => {
    setIsLoading(true);
    // Fetch the full calendar-view range (start of first week → end of last week
    // shown for the current month, so overflow days are also populated).
    const calStart = currentMonth.clone().startOf("month").startOf("week").format("YYYY-MM-DD");
    const calEnd = currentMonth.clone().endOf("month").endOf("week").format("YYYY-MM-DD");
    try {
      const { data } = await FirebaseDataService.getScheduledWorkOrders(loggedInUserId, isManager, calStart, calEnd);
      setWorkOrders(data?.workOrders ?? []);
    } catch {
      setWorkOrders([]);
    } finally {
      setIsLoading(false);
    }
  }, [loggedInUserId, isManager, currentMonth]);

  useEffect(() => {
    fetchWorkOrders();
  }, [currentMonth]);

  // Group by date key YYYY-MM-DD using schedstart first, then actstart.
  // Keep targstartdate as a final fallback for older records.
  const workOrdersByDate = useMemo(() => {
    const map: Record<string, Ticket[]> = {};
    workOrders.forEach((wo) => {
      const dateStr = wo.schedstart ?? wo.actstart ?? wo.targstartdate;
      if (dateStr) {
        const key = moment(dateStr).format("YYYY-MM-DD");
        if (!map[key]) map[key] = [];
        map[key].push(wo);
      }
    });
    return map;
  }, [workOrders]);

  const calendarDays = useMemo(() => buildCalendarDays(currentMonth), [currentMonth]);

  const selectedDayOrders: Ticket[] = selectedDay ? (workOrdersByDate[selectedDay.format("YYYY-MM-DD")] ?? []) : [];

  const prevMonth = () => setCurrentMonth((m) => m.clone().subtract(1, "month"));
  const nextMonth = () => setCurrentMonth((m) => m.clone().add(1, "month"));

  const goToToday = () => {
    setCurrentMonth(moment().startOf("month"));
    setSelectedDay(moment().startOf("day"));
  };

  return (
    <PageWrapper>
      {/* ── Top header bar ── */}
      <TopBar>
        <TitleGroup>
          <IconCircle>
            <FontAwesomeIcon icon={faCalendarDays} />
          </IconCircle>
          <TitleBlock>
            <PageTitle>Schedules</PageTitle>
            <PageSub>Planned maintenance calendar</PageSub>
          </TitleBlock>
        </TitleGroup>
      </TopBar>

      {/* ── Main layout ── */}
      <Body>
        {/* ─ Calendar ─ */}
        <CalendarSection>
          <CalendarHeader>
            <NavBtn onClick={prevMonth} title="Previous month">
              <FontAwesomeIcon icon={faChevronLeft} />
            </NavBtn>
            <MonthTitle>{currentMonth.format("MMMM YYYY")}</MonthTitle>
            <NavBtn onClick={nextMonth} title="Next month">
              <FontAwesomeIcon icon={faChevronRight} />
            </NavBtn>
            <TodayBtn onClick={goToToday}>Today</TodayBtn>
          </CalendarHeader>

          {isLoading ? (
            <LoadingBox>
              <Spinner animation="border" style={{ color: colors.kleenway.green }} />
              <LoadingText>Loading schedules…</LoadingText>
            </LoadingBox>
          ) : (
            <CalendarGrid>
              {WEEK_DAYS.map((d) => (
                <WeekLabel key={d}>{d}</WeekLabel>
              ))}

              {calendarDays.map((day) => {
                const key = day.format("YYYY-MM-DD");
                const orders = workOrdersByDate[key] ?? [];
                const inMonth = day.isSame(currentMonth, "month");
                const isToday = day.isSame(moment(), "day");
                const isSelected = !!selectedDay && day.isSame(selectedDay, "day");

                return (
                  <DayCell
                    key={key}
                    $inMonth={inMonth}
                    $isSelected={isSelected}
                    $isToday={isToday}
                    $hasOrders={orders.length > 0}
                    onClick={() => setSelectedDay(day.clone())}
                  >
                    <DayRow>
                      <DayNum $isToday={isToday} $isSelected={isSelected}>
                        {day.date()}
                      </DayNum>
                      {orders.length > 0 && <OrderCount>{orders.length}</OrderCount>}
                    </DayRow>

                    <ChipList>
                      {(() => {
                        // Group by distinct location
                        const locationMap: Record<string, number> = {};
                        orders.forEach((wo) => {
                          const loc = wo.location ?? wo.wonum;
                          locationMap[loc] = (locationMap[loc] ?? 0) + 1;
                        });
                        const locations = Object.entries(locationMap);
                        const visible = locations.slice(0, 2);
                        const hiddenCount = locations.length - visible.length;
                        return (
                          <>
                            {visible.map(([loc, count]) => {
                              // Use the first WO's status for colour
                              const wo = orders.find((o) => (o.location ?? o.wonum) === loc)!;
                              const p = getStatusPalette(wo.statusDescription);
                              return (
                                <WoChip
                                  key={loc}
                                  $bg={p.bg}
                                  $color={p.text}
                                  $border={p.border}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedDay(day.clone());
                                  }}
                                  title={loc}
                                >
                                  <ChipDot $color={p.text} />
                                  <ChipLabel>{loc}</ChipLabel>
                                  {count > 1 && <ChipBadge $color={p.text}>{count}</ChipBadge>}
                                </WoChip>
                              );
                            })}
                            {hiddenCount > 0 && <MoreLabel>+{hiddenCount} more</MoreLabel>}
                          </>
                        );
                      })()}
                    </ChipList>
                  </DayCell>
                );
              })}
            </CalendarGrid>
          )}
        </CalendarSection>

        {/* ─ Day detail panel ─ */}
        <DetailPanel $visible={selectedDay !== null}>
          {selectedDay && (
            <>
              <PanelHead>
                <PanelDateBlock>
                  <PanelDayName>{selectedDay.format("dddd")}</PanelDayName>
                  <PanelFullDate>{selectedDay.format("MMMM D, YYYY")}</PanelFullDate>
                </PanelDateBlock>
                <CloseBtn onClick={() => setSelectedDay(null)} title="Close">
                  <FontAwesomeIcon icon={faXmark} />
                </CloseBtn>
              </PanelHead>

              <PanelDivider />

              {selectedDayOrders.length === 0 ? (
                <EmptyState>
                  <FontAwesomeIcon icon={faClipboardList} style={{ fontSize: 32, opacity: 0.25 }} />
                  <EmptyMsg>No work orders scheduled for this day</EmptyMsg>
                </EmptyState>
              ) : (
                <CardList>
                  <CardListTitle>
                    {selectedDayOrders.length} work order{selectedDayOrders.length !== 1 ? "s" : ""}
                  </CardListTitle>
                  {selectedDayOrders.map((wo) => {
                    const p = getStatusPalette(wo.statusDescription);
                    return (
                      <WoCard key={wo.wonum} onClick={() => navigate(`/workrequests/${wo.wonum}`)}>
                        <CardAccent $color={p.border} />
                        <CardBody>
                          <CardRow>
                            <WoNumber>#{wo.wonum}</WoNumber>
                            <StatusPill $bg={p.bg} $color={p.text} $border={p.border}>
                              {wo.statusDescription ?? "—"}
                            </StatusPill>
                          </CardRow>

                          <WoDesc>{wo.description ?? "No description"}</WoDesc>

                          <MetaGroup>
                            {wo.location && (
                              <MetaLine>
                                <FontAwesomeIcon icon={faMapMarkerAlt} />
                                <span>{wo.location}</span>
                              </MetaLine>
                            )}
                            {wo.serviceType && (
                              <MetaLine>
                                <FontAwesomeIcon icon={faTag} />
                                <span>{wo.serviceType}</span>
                              </MetaLine>
                            )}
                          </MetaGroup>

                          {wo.schedfinish && (
                            <DueLine>
                              <FontAwesomeIcon icon={faCircleCheck} />
                              Due {moment(wo.schedfinish).format("MMM D, YYYY")}
                            </DueLine>
                          )}

                          <ViewLink>
                            View details <FontAwesomeIcon icon={faArrowRight} />
                          </ViewLink>
                        </CardBody>
                      </WoCard>
                    );
                  })}
                </CardList>
              )}
            </>
          )}
        </DetailPanel>
      </Body>
    </PageWrapper>
  );
};

export default Schedules;

// ─── Animations ──────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(20px); }
  to   { opacity: 1; transform: translateX(0); }
`;

// ─── Layout ──────────────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  background: ${colors.grey.light14};
`;

const TopBar = styled.div`
  background: ${colors.kleenway.backgroundDark};
  padding: 22px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const IconCircle = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(129, 193, 125, 0.18);
  border: 1px solid rgba(129, 193, 125, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: ${colors.kleenway.green};
  flex-shrink: 0;
`;

const TitleBlock = styled.div``;

const PageTitle = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: ${colors.white.default};
  margin: 0;
  line-height: 1.2;
`;

const PageSub = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.45);
  margin: 2px 0 0;
`;

const Body = styled.div`
  display: flex;
  flex: 1;
  gap: 0;
  overflow: hidden;
`;

// ─── Calendar section ─────────────────────────────────────────────────────────

const CalendarSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 24px;
  min-width: 0;
`;

const CalendarHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const NavBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid ${colors.grey.light1};
  background: ${colors.white.default};
  color: ${colors.grey.dark};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  transition: all 0.15s;

  &:hover {
    background: ${colors.kleenway.backgroundDark};
    color: ${colors.white.default};
    border-color: ${colors.kleenway.backgroundDark};
  }
`;

const MonthTitle = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: ${colors.black.light2};
  min-width: 170px;
  text-align: center;
`;

const TodayBtn = styled.button`
  margin-left: 4px;
  padding: 6px 14px;
  border-radius: 8px;
  border: 1px solid ${colors.kleenway.green};
  background: transparent;
  color: ${colors.kleenway.backgroundDark};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${colors.kleenway.green};
    color: ${colors.white.default};
  }
`;

const LoadingBox = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px;
`;

const LoadingText = styled.span`
  font-size: 14px;
  color: ${colors.grey.dark5};
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid ${colors.grey.light1};
  background: ${colors.white.default};
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  animation: ${fadeIn} 0.25s ease;
`;

const WeekLabel = styled.div`
  background: ${colors.kleenway.backgroundDark};
  color: rgba(255, 255, 255, 0.65);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  text-align: center;
  padding: 10px 0;
`;

const DayCell = styled.div<{
  $inMonth: boolean;
  $isSelected: boolean;
  $isToday: boolean;
  $hasOrders: boolean;
}>`
  border-right: 1px solid ${colors.grey.light3};
  border-bottom: 1px solid ${colors.grey.light3};
  min-height: 110px;
  padding: 8px;
  cursor: pointer;
  transition: background 0.15s;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  overflow: hidden;

  background: ${({ $isSelected, $inMonth }) => {
    if ($isSelected) return "rgba(129, 193, 125, 0.12)";
    if (!$inMonth) return colors.grey.light14;
    return colors.white.default;
  }};

  ${({ $isSelected }) =>
    $isSelected &&
    css`
      box-shadow: inset 0 0 0 2px ${colors.kleenway.green};
    `}

  &:hover {
    background: ${({ $isSelected }) => ($isSelected ? "rgba(129,193,125,0.16)" : colors.grey.light7)};
  }

  &:nth-child(7n) {
    border-right: none;
  }
`;

const DayRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DayNum = styled.span<{ $isToday: boolean; $isSelected: boolean }>`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: ${({ $isToday, $isSelected }) => ($isToday || $isSelected ? 700 : 500)};
  color: ${({ $isToday }) => ($isToday ? colors.white.default : colors.black.light1)};
  background: ${({ $isToday }) => ($isToday ? colors.kleenway.backgroundDark : "transparent")};
  flex-shrink: 0;
`;

const OrderCount = styled.span`
  font-size: 10px;
  font-weight: 700;
  background: ${colors.kleenway.green};
  color: ${colors.white.default};
  border-radius: 10px;
  padding: 1px 6px;
  min-width: 18px;
  text-align: center;
`;

const ChipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: 2px;
`;

const WoChip = styled.div<{ $bg: string; $color: string; $border: string }>`
  background: ${({ $bg }) => $bg};
  border-left: 3px solid ${({ $border }) => $border};
  border-radius: 0 4px 4px 0;
  padding: 2px 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  transition: opacity 0.1s;
  min-width: 0;
  overflow: hidden;

  &:hover {
    opacity: 0.75;
  }
`;

const ChipDot = styled.span<{ $color: string }>`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const ChipLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: ${colors.black.light1};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChipBadge = styled.span<{ $color: string }>`
  font-size: 9px;
  font-weight: 700;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => $color}22;
  border-radius: 8px;
  padding: 0px 5px;
  flex-shrink: 0;
  line-height: 16px;
`;

const MoreLabel = styled.span`
  font-size: 10px;
  color: ${colors.grey.dark5};
  padding-left: 4px;
`;

// ─── Detail panel ─────────────────────────────────────────────────────────────

const DetailPanel = styled.div<{ $visible: boolean }>`
  width: ${({ $visible }) => ($visible ? "340px" : "0")};
  overflow: hidden;
  background: ${colors.white.default};
  border-left: 1px solid ${colors.grey.light1};
  display: flex;
  flex-direction: column;
  transition: width 0.25s ease;
  flex-shrink: 0;
`;

const PanelHead = styled.div`
  padding: 20px 20px 16px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  animation: ${slideIn} 0.2s ease;
`;

const PanelDateBlock = styled.div``;

const PanelDayName = styled.div`
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: ${colors.kleenway.green};
`;

const PanelFullDate = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${colors.black.light2};
  margin-top: 2px;
`;

const CloseBtn = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid ${colors.grey.light1};
  background: ${colors.grey.light14};
  color: ${colors.grey.dark};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
  transition: all 0.15s;

  &:hover {
    background: ${colors.kleenway.backgroundDark};
    color: ${colors.white.default};
    border-color: ${colors.kleenway.backgroundDark};
  }
`;

const PanelDivider = styled.hr`
  margin: 0;
  border: none;
  border-top: 1px solid ${colors.grey.light1};
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px 20px;
  color: ${colors.grey.dark5};
`;

const EmptyMsg = styled.p`
  font-size: 13px;
  color: ${colors.grey.dark5};
  text-align: center;
  margin: 0;
`;

const CardList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CardListTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: ${colors.grey.dark5};
  margin-bottom: 4px;
`;

const WoCard = styled.div`
  display: flex;
  border-radius: 10px;
  border: 1px solid ${colors.grey.light1};
  overflow: hidden;
  cursor: pointer;
  transition:
    box-shadow 0.15s,
    transform 0.15s;
  animation: ${fadeIn} 0.2s ease;
  background: ${colors.white.default};

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }
`;

const CardAccent = styled.div<{ $color: string }>`
  width: 4px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const CardBody = styled.div`
  padding: 12px 14px;
  flex: 1;
  min-width: 0;
`;

const CardRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 6px;
`;

const WoNumber = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${colors.black.light3};
`;

const StatusPill = styled.span<{ $bg: string; $color: string; $border: string }>`
  font-size: 10px;
  font-weight: 700;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  border: 1px solid ${({ $border }) => $border};
  border-radius: 20px;
  padding: 2px 8px;
  white-space: nowrap;
`;

const WoDesc = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.black.light2};
  margin: 0 0 8px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const MetaGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 6px;
`;

const MetaLine = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: ${colors.grey.dark5};

  svg {
    font-size: 10px;
    flex-shrink: 0;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const DueLine = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: ${colors.orange.default};
  font-weight: 600;
  margin-bottom: 8px;

  svg {
    font-size: 11px;
  }
`;

const ViewLink = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 700;
  color: ${colors.kleenway.green};
  margin-top: 2px;

  svg {
    font-size: 10px;
    transition: transform 0.15s;
  }

  ${WoCard}:hover & svg {
    transform: translateX(3px);
  }
`;
